import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { Observable } from 'rxjs';

import { BaseHttpReqRespService } from '../../../modules/fwcore/services';
import {
  RequestRateDashDetail, RequestRateDashFilterOptions, RequestRateDashSummary,
  ResponseRateDashDetail, ResponseRateDashFilterOptions, ResponseRateDashSummary
} from '../models';

/**
 * HTTP access to the IT Labor Rate Dashboard endpoints. No state - see
 * RateDashboardStateService for the debounce, cancellation and caching.
 *
 * Unsubscribing from any of these aborts the HTTP request, and the API passes that on to
 * SQL Server, so a superseded query stops on the server too.
 */
@Injectable({
  providedIn: 'root'
})
export class RateDashboardApiService extends BaseHttpReqRespService {

  // ----------------- Constants ---------------------------------------------

  readonly endPointFilterOptions: string = 'ratedashboard/filter-options';
  readonly endPointSummary: string = 'ratedashboard/summary';
  readonly endPointDetail: string = 'ratedashboard/detail';

  // ----------------- Constructor -------------------------------------------

  constructor(
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,
    oktaWrapper: OktaWrapperService,
    impersonation: ImpersonationService
  ) {
    super(http, config, impersonation, oktaWrapper);
  }

  // ----------------- Reads --------------------------------------------------

  /**
   * Gets the rate cards, display currencies, the filter values still available given the
   * other filters (with counts), and the YoE slider bounds.
   */
  public filterOptionsGet$(req: RequestRateDashFilterOptions): Observable<ResponseRateDashFilterOptions> {

    return this.getData$<RequestRateDashFilterOptions, ResponseRateDashFilterOptions>(req, this.endPointFilterOptions);
  }

  /**
   * Gets the KPIs and the by-country, by-role, by-YoE and by-confidence aggregates.
   * Unfiltered requests come from the server-side cache in well under a second.
   */
  public summaryGet$(req: RequestRateDashSummary): Observable<ResponseRateDashSummary> {

    return this.getData$<RequestRateDashSummary, ResponseRateDashSummary>(req, this.endPointSummary);
  }

  /**
   * Gets one sorted page of the filtered combinations, with the total across all pages.
   */
  public detailGet$(req: RequestRateDashDetail): Observable<ResponseRateDashDetail> {

    return this.getData$<RequestRateDashDetail, ResponseRateDashDetail>(req, this.endPointDetail);
  }
}
